// Página de consultas de notas fiscais
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Table } from '../../components/common/Table';
import { Badge } from '../../components/common/Badge';
import { Breadcrumbs } from '../../components/layout/Breadcrumbs';
import { Loading } from '../../components/common/Loading';
import { Search, Download, Eye, AlertCircle } from 'lucide-react';
import { getNotasFiscais } from '../../services/notasFiscaisService';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import type { NotaFiscal } from '../../types';

export function Consultas() {
  const navigate = useNavigate();
  const { empresaSelecionada } = useEmpresa();
  const [filtros, setFiltros] = useState({
    busca: '',
    tipo: '',
    status: '',
    dataInicio: '',
    dataFim: '',
  });
  const [notas, setNotas] = useState<NotaFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (empresaSelecionada) {
      carregarNotas();
    }
  }, [empresaSelecionada]);

  const carregarNotas = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getNotasFiscais(empresaSelecionada.id);
      setNotas(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar notas fiscais');
      console.error('Erro ao carregar notas fiscais:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const notasFiltradas = notas.filter(nota => {
    const matchBusca = !filtros.busca || 
      nota.numero.includes(filtros.busca) ||
      nota.destinatario.nome.toLowerCase().includes(filtros.busca.toLowerCase()) ||
      (nota.chaveAcesso && nota.chaveAcesso.includes(filtros.busca));
    const matchTipo = !filtros.tipo || nota.tipo === filtros.tipo;
    const matchStatus = !filtros.status || nota.status === filtros.status;
    
    // Filtro por data
    let matchData = true;
    if (filtros.dataInicio || filtros.dataFim) {
      const dataEmissao = new Date(nota.dataEmissao);
      if (filtros.dataInicio) {
        const dataInicio = new Date(filtros.dataInicio);
        if (dataEmissao < dataInicio) matchData = false;
      }
      if (filtros.dataFim) {
        const dataFim = new Date(filtros.dataFim);
        dataFim.setHours(23, 59, 59, 999);
        if (dataEmissao > dataFim) matchData = false;
      }
    }
    
    return matchBusca && matchTipo && matchStatus && matchData;
  });
  
  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Consultas' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar as notas fiscais</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Consultas' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Consultas' }]} />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Consultar Notas Fiscais</h1>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      {/* Filtros */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Filtros de Busca
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Buscar"
            placeholder="Número, cliente, chave..."
            value={filtros.busca}
            onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
          />
          <Select
            label="Tipo de Nota"
            value={filtros.tipo}
            onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
            options={[
              { value: '', label: 'Todos' },
              { value: 'NFe', label: 'NFe' },
              { value: 'NFSe', label: 'NFSe' },
              { value: 'NFCe', label: 'NFCe' },
            ]}
          />
          <Select
            label="Status"
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
            options={[
              { value: '', label: 'Todos' },
              { value: 'emitida', label: 'Emitida' },
              { value: 'rascunho', label: 'Rascunho' },
              { value: 'cancelada', label: 'Cancelada' },
              { value: 'erro', label: 'Erro' },
            ]}
          />
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={carregarNotas} className="w-full">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Input
            label="Data Início"
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
          />
          <Input
            label="Data Fim"
            type="date"
            value={filtros.dataFim}
            onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
          />
        </div>
      </Card>
      
      {/* Resultados */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Resultados ({notasFiltradas.length})
          </h2>
        </div>
        
        {notasFiltradas.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Nenhuma nota fiscal encontrada</p>
          </div>
        ) : (
          <Table
            data={notasFiltradas}
            columns={[
              { key: 'tipo', label: 'Tipo' },
              { key: 'numero', label: 'Número', render: (item) => `${item.numero}/${item.serie}` },
              { key: 'destinatario', label: 'Destinatário', render: (item) => item.destinatario.nome },
              { key: 'dataEmissao', label: 'Data Emissão', render: (item) => formatDate(item.dataEmissao) },
              { key: 'total', label: 'Total', render: (item) => formatCurrency(item.total) },
              {
                key: 'status',
                label: 'Status',
                render: (item) => (
                  <Badge
                    variant={
                      item.status === 'emitida' ? 'success' :
                      item.status === 'cancelada' ? 'danger' :
                      item.status === 'rascunho' ? 'warning' : 'default'
                    }
                  >
                    {item.status}
                  </Badge>
                ),
              },
              {
                key: 'actions',
                label: 'Ações',
                render: (item) => (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => navigate(`/app/notas/${item.id}`)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
